import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { productApi } from '../services/productsApi'
import { Product, ProductCreate, ProductUpdate } from '../types/product'

// Query keys
export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (activeOnly?: boolean) => [...productKeys.lists(), { activeOnly }] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: number) => [...productKeys.details(), id] as const,
  search: (query: string, activeOnly?: boolean) =>
    [...productKeys.all, 'search', query, { activeOnly }] as const,
}

// List all products
export function useProducts(activeOnly: boolean = false) {
  return useQuery({
    queryKey: productKeys.list(activeOnly),
    queryFn: () => productApi.getAll(activeOnly),
  })
}

// Get single product
export function useProduct(id: number) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => productApi.getById(id),
    enabled: !!id,
  })
}

// Search products
export function useSearchProducts(query: string, activeOnly: boolean = true) {
  return useQuery({
    queryKey: productKeys.search(query, activeOnly),
    queryFn: () => productApi.search(query, activeOnly),
    enabled: query.length >= 1,
  })
}

// Create product
export function useCreateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ProductCreate) => productApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

// Update product
export function useUpdateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ProductUpdate }) =>
      productApi.update(id, data),
    onSuccess: (updatedProduct: Product) => {
      queryClient.setQueryData(
        productKeys.detail(updatedProduct.id),
        updatedProduct
      )
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

// Delete product
export function useDeleteProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => productApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: productKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}
